// Extraordinary casts share preparation membership, class limits and the native ability transaction.
import { scalarLayerSlot, layerOf } from '../../shared/array-types.js';
import { casterClasses, casterMeetsSpellFeats, classPoolAtLevel, poolKeyOf, poolKeysForRow, type PoolEntry, type PoolPayload } from '../spells/spell-prep-table.js';
import { spellcastingLimits, phpInt } from '../spells/spell-prep-limits.js';
import { prepPoolFromDefinitions, type PrepInputs } from '../spells/spell-prep-inputs.js';
import { abilityEntryId, CHARGE_BUCKETS, type ChargeReaders } from '../charges/charge-adjust.js';
import { chargeSpendWaivedPools, adjustCharges } from '../ability/charges/adjust-charges.js';
import { readCreaturePayloadStatuses } from '../compile/creature-status-reader.js';
import { itemField, record, upper, refuse, type Row, type JanusCtx } from '../ability/context.js';
import { extraordinaryCastGate, sanguinePoolCurrent, SANGUINE_POOL_ID } from './sanguine-gate.js';
import type { PlacedAoePlacement } from '../ability/aoe/expand-placed-aoe-targets.js';
import type { SummonPoint } from '../ability/summon.js';
import { chargeReductionDelta } from '../resources/reduction-guard.js';
import { logPreventedReduction, resourceReductionReaders } from '../ability/resource-reduction.js';

export type ExtraordinaryMode = 'adlib' | 'voidgrasp';
export interface ExtraordinaryCast { mode: ExtraordinaryMode; pool: PoolPayload }
export interface ExtraordinaryTargets {
    targetIds: string[]; selection?: string; placed?: PlacedAoePlacement;
    placementProof?: Row; summon?: { point: SummonPoint; points?: SummonPoint[]; pick?: string };
}
export const EXTRAORDINARY_IDS = { adlib: '4D7142B0-96DE-3DAB-14CE-DBA40FAF47E8', voidgrasp: '052C008A-1E70-D828-50A5-50192772B8C4' } as const;
export interface ExtraordinaryReaders extends ChargeReaders { definition: (id: string) => Row | null }
export type ExtraordinaryChoice = PoolEntry & { level: number; summonChoices: Array<{ id: string; name: string }> };

/** SELECT input adapter; signatures come from the browser's current caster classes. */
function classRow(caster: Row, read: Pick<ChargeReaders, 'value'>): Row {
    const pc = { ...record(caster.player_characters) };
    for (let n = 1; n <= 20; n++) for (const field of [`class_${n}`, `class_level_${n}`, `subclass_${n}`])
        pc[field] = pc[field] ?? read.value(caster, field);
    return { ...caster, player_characters: pc };
}
export function extraordinaryPool(caster: Row, input: PrepInputs, read: Pick<ChargeReaders, 'value'> = { value: layerOf }): PoolPayload {
    return prepPoolFromDefinitions({ ...input, signatures: poolKeysForRow(classRow(caster, read)) }, input.base.catalog);
}

/** The same class pool as Prepare; no known-spell additions from other spheres. */
export function extraordinaryChoices(caster: Row, mode: ExtraordinaryMode, pool: PoolPayload, read: ExtraordinaryReaders): ExtraordinaryChoice[] {
    caster = classRow(caster, read);
    const found = new Map<string, ExtraordinaryChoice>();
    for (const cls of casterClasses(caster, pool.meta, { nameOf: () => '' })) {
        const pc = record(caster.player_characters);
        const level = phpInt(scalarLayerSlot(pc?.[`class_level_${cls.class_number}`] ?? read.value(caster, `class_level_${cls.class_number}`)));
        const limits = spellcastingLimits(pool.meta.find(m => m.item_id === cls.class_id), level, caster);
        const max = limits.max_spell_level_castable;
        if (max < 1) continue;
        const entries = pool.pools[poolKeyOf(caster, cls.class_number)];
        if (entries === undefined) throw new Error('sanguine:class-pool-not-held');
        for (const spell of classPoolAtLevel(entries ?? [], level, mode === 'voidgrasp' ? max + 1 : max, limits.max_class_cantrips_known)) {
            const spellLevel = phpInt(spell.level);
            if ((mode === 'voidgrasp' ? spellLevel !== max + 1 : spellLevel < 1 || spellLevel > max)
                || !casterMeetsSpellFeats(caster, spell.feats) || found.has(upper(spell.id))) continue;
            const summonChoices = read.entries(spell.nest, 'user_choice_1_summon').map(entry => {
                const id = abilityEntryId(entry);
                return { id, name: String(itemField(read.definition(id) ?? record(entry), 'item_name') ?? id) };
            }).filter(choice => !!choice.id);
            found.set(upper(spell.id), { ...spell, level: spellLevel, summonChoices });
        }
    }
    return [...found.values()].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
}

export function extraordinaryQuote(caster: Row, mode: ExtraordinaryMode, read: ExtraordinaryReaders): { cost: number; waived: boolean } {
    const statuses = Object.values(readCreaturePayloadStatuses(caster, (row, key) => read.value(record(row), key), read.definition)).map(record).filter((row): row is Row => !!row);
    const waived = chargeReductionDelta(caster, -1, read) === 0 || chargeSpendWaivedPools({ read }, statuses).has(SANGUINE_POOL_ID);
    return { cost: waived ? 0 : mode === 'voidgrasp' ? 5 : 3, waived };
}

/** Run on the current actor before any die. A missing pool never means allow. */
export function extraordinaryAuthorize(caster: Row, spellId: string, cast: ExtraordinaryCast, read: ExtraordinaryReaders): { cost: number; waived: boolean } {
    const held = (id: string): boolean => CHARGE_BUCKETS.some(bucket => read.entries(caster, bucket).some(entry => abilityEntryId(entry) === id));
    if (!held(EXTRAORDINARY_IDS[cast.mode])) refuse('This caster does not have that Sanguine.');
    const pool = sanguinePoolCurrent(caster, read);
    if (pool === null) refuse('The caster’s Sanguine pool is unavailable.');
    if (!extraordinaryChoices(caster, cast.mode, cast.pool, read).some(spell => upper(spell.id) === upper(spellId)))
        refuse('That spell is outside this Sanguine’s eligible class pool or level window.');
    const quote = extraordinaryQuote(caster, cast.mode, read);
    const gate = extraordinaryCastGate(quote.cost, pool, quote.waived);
    if (gate.kind === 'refuse') refuse(gate.message);
    return quote;
}

/** Debit joins the spell's existing logs, receipt, CAS and replay in one ability save. */
export function spendExtraordinary(ctx: JanusCtx, caster: Row, quote: { cost: number }): void {
    if (quote.cost) adjustCharges(ctx, caster, SANGUINE_POOL_ID, -quote.cost, true);
    else if (chargeReductionDelta(caster, -1, resourceReductionReaders(ctx)) === 0) logPreventedReduction(ctx, caster, 'charges');
}
